import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  BarChart3,
  Wallet,
  Upload,
  Sparkles,
  Tag,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { tapFeedback } from "@/lib/utils";

/**
 * 6-tab bottom navigation bar. Single source of truth for navigation across
 * the entire app — public pages AND the post-login Dashboard.
 *
 *   Home · Spending · Budget · Upload · Intelligence · Pricing
 *
 * Behaviour:
 *   • On the public Landing route ("/"), Home scrolls to top.
 *   • On `/dashboard/*`, Home / Spending / Budget switch the dashboard tab
 *     via URL hash so the page state stays in place. Upload / Intelligence
 *     / Pricing always navigate to their dedicated routes.
 *   • Anywhere else, every tab navigates to its dedicated route.
 *
 * The About modal is no longer in the bottom nav — it's still reachable
 * from the "About Us" button in the public header.
 *
 * Active state highlights whichever tab matches the current location
 * (pathname for normal routes, hash for /dashboard/*).
 */
const TABS = [
  { id: "home", label: "My Dashboard", shortLabel: "Dashboard", icon: Home, route: "/", dashHash: "home", match: "/" },
  { id: "spending", label: "Spending", shortLabel: "Spend", icon: BarChart3, route: "/spending", dashHash: "spending", match: "/spending" },
  { id: "budget", label: "Budget", shortLabel: "Budget", icon: Wallet, route: "/budget", dashHash: "budget", match: "/budget" },
  // Upload always navigates to the dedicated /upload page — no in-dashboard
  // hash variant — because the real drop-zone + processing pipeline lives
  // there, not on the Dashboard.
  { id: "upload", label: "Upload", shortLabel: "Upload", icon: Upload, route: "/upload", match: "/upload" },
  {
    id: "intelligence",
    label: "Intelligence",
    shortLabel: "Intel",
    icon: Sparkles,
    route: "/intelligence",
    match: "/intelligence",
  },
  { id: "pricing", label: "Pricing", shortLabel: "Pricing", icon: Tag, route: "/pricing", match: "/pricing" },
];

export { TABS };

const isDashboardPath = (p) => p.startsWith("/dashboard/");

export default function LandingBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { meta } = useAuth();
  const latestReportId = meta?.latestReportId || null;
  const [layoutMode, setLayoutMode] = useState("full"); // full | short | icons
  // Track the hash so dashboard-tab active state updates as the user
  // switches tabs without changing pathname.
  const [hash, setHash] = useState(
    typeof window !== "undefined" ? window.location.hash.replace("#", "") : ""
  );

  // Three responsive tiers for the 7-tab bar:
  //   • <360px → icons only (very narrow androids)
  //   • <480px → short labels ("Spend", "Intel") so 7 tabs still fit
  //   • >=480px → full labels
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 360) setLayoutMode("icons");
      else if (w < 480) setLayoutMode("short");
      else setLayoutMode("full");
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Keep `hash` in sync so the active dashboard tab updates when the user
  // taps a tab inside the Dashboard.
  useEffect(() => {
    const onHash = () =>
      setHash(window.location.hash.replace("#", ""));
    window.addEventListener("hashchange", onHash);
    onHash();
    return () => window.removeEventListener("hashchange", onHash);
  }, [location]);

  const onDashboard = isDashboardPath(location.pathname);

  // Active tab resolution.
  //   • On /dashboard/* → use hash to pick the in-app tab; default "home".
  //   • On "/" → "home".
  //   • Else → match by pathname prefix.
  const activeId = (() => {
    if (onDashboard) {
      const h = hash || "home";
      const t = TABS.find((tab) => tab.dashHash === h);
      return t?.id || "home";
    }
    const p = location.pathname;
    if (p === "/") return "home";
    const t = TABS.find(
      (tab) => tab.match && tab.match !== "/" && p.startsWith(tab.match)
    );
    return t?.id || null;
  })();

  const handleTab = (tab) => {
    tapFeedback();
    // 1. Already on the dashboard? In-app tabs (Home/Spending/Budget) just
    //    flip the URL hash so the dashboard re-renders without a route
    //    change.
    if (onDashboard && tab.dashHash) {
      const target = `${location.pathname}#${tab.dashHash}`;
      navigate(target);
      return;
    }
    // 2. Authed user on a non-dashboard route? Spending/Budget should jump
    //    DIRECTLY into their dashboard at the matching hash, skipping the
    //    public IntentGate redirect (which was leaving users stranded if
    //    the auth context had not finished loading yet — i.e. the bug
    //    where users had to hit Back before tabs would respond).
    if (tab.dashHash && latestReportId && (tab.id === "spending" || tab.id === "budget")) {
      navigate(`/dashboard/${latestReportId}#${tab.dashHash}`);
      return;
    }
    if (tab.route) {
      // Same-route click on Home → scroll to top for a "back to start" feel.
      if (tab.id === "home" && location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      navigate(tab.route);
    }
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0F]/95 backdrop-blur-md border-t border-[#2A2A35] grid grid-cols-6 lg:hidden"
      style={{
        paddingLeft: "calc(8px + env(safe-area-inset-left))",
        paddingRight: "calc(8px + env(safe-area-inset-right))",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="landing-bottom-nav"
    >
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeId === tab.id;
        const showLabel = layoutMode !== "icons";
        const text = layoutMode === "short" ? (tab.shortLabel || tab.label) : tab.label;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTab(tab)}
            className={`flex flex-col items-center justify-center gap-0.5 py-2.5 px-0.5 transition-colors ${
              isActive
                ? "text-[#FF6B00]"
                : "text-[#555555] hover:text-[#F0EDE8]"
            }`}
            data-testid={`landing-tab-${tab.id}`}
            aria-label={tab.label}
            aria-current={isActive ? "page" : undefined}
          >
            <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
            {showLabel && (
              <span className="text-[9px] uppercase tracking-[0.06em] font-bold leading-none mt-1 truncate max-w-full">
                {text}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
